import { useSelector } from 'react-redux'
import { checkIfAIVideoIsGenerating } from 'containers/SubSidebar/AIVideoContent/utils'
import { checkIfAIImageIsGenerating } from 'containers/SubSidebar/AiImageContent/utils'
import ActionButton from '../ActionButton'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import useDurationLimitCheck from 'hooks/useDurationLimitCheck'
import DuplicateIcon from 'assets/svgr-icons/Duplicate.svg'
import useAudioLimitCheck from 'hooks/useAudioLimitCheck'
import { useCallback, useMemo } from 'react'
import { editorThunks } from 'store/features/editor/thunks'
import { editorSelectors } from 'store/features/editor/selectors'
import { useKeyPressWithConfigs } from 'hooks'
import { isVisualMediaItem } from 'utils/media'
import { isAudioItem } from 'utils/audio'
import { IMediaItem } from 'types/media'
import { IAudioItem } from 'types/audio'
import { canCopyMediaItem, canCopyAudioItem } from 'utils/timeline'
import { useResponsive } from 'hooks/useResponsive'
import { MOBILE_SIZE_BREAKPOINT } from 'consts'

export const DuplicateButton = () => {
  const activeMediaItem = useSelector(projectDataSelectors.activeMedia)
  const activeAudioItem = useSelector(projectDataSelectors.activeAudio)
  const clipboardItem = useSelector(editorSelectors.clipboard)
  const isMobile = useResponsive(MOBILE_SIZE_BREAKPOINT)
  const { isAudioDurationAllowed } = useAudioLimitCheck()
  const { isDurationAllowed } = useDurationLimitCheck()

  const isAIContentGenerating = activeMediaItem
    ? checkIfAIImageIsGenerating(activeMediaItem) ||
      checkIfAIVideoIsGenerating(activeMediaItem)
    : false

  const isDisabledForMedia =
    !canCopyMediaItem(activeMediaItem) || isAIContentGenerating

  const isDisabledForAudio = !canCopyAudioItem(activeAudioItem)

  const isDisabled = useMemo(() => {
    if (activeMediaItem) {
      return isDisabledForMedia
    }
    if (activeAudioItem) {
      return isDisabledForAudio
    }
    return true
  }, [activeAudioItem, activeMediaItem, isDisabledForAudio, isDisabledForMedia])

  const duplicateMediaItem = useCallback(
    (mediaItem: IMediaItem) => {
      if (!isDurationAllowed(mediaItem.duration)) {
        return
      }
      const duplicatedItemId = dispatch(
        projectDataThunks.duplicateVisualMediaItem(mediaItem.id)
      )
      if (duplicatedItemId) {
        dispatch(
          editorThunks.selectSingleTimelineItem({
            id: duplicatedItemId,
            type: mediaItem.type
          })
        )
      }
    },
    [isDurationAllowed]
  )

  const duplicateAudioItem = useCallback(
    (audioItem: IAudioItem) => {
      if (
        !isAudioDurationAllowed(audioItem.duration, {
          checkLineOnly: true,
          lineIndex: audioItem.lineIndex
        })
      ) {
        return
      }
      const duplicatedItemId = dispatch(
        projectDataThunks.duplicateAudioItem(audioItem.id)
      )
      if (duplicatedItemId) {
        dispatch(
          editorThunks.selectSingleTimelineItem({
            id: duplicatedItemId,
            type: audioItem.type
          })
        )
      }
    },
    [isAudioDurationAllowed]
  )

  const duplicateTimelineItem = useCallback(
    (item: IMediaItem | IAudioItem) => {
      if (isVisualMediaItem(item.type)) {
        duplicateMediaItem(item as IMediaItem)
      } else if (isAudioItem(item.type)) {
        duplicateAudioItem(item as IAudioItem)
      }
    },
    [duplicateMediaItem, duplicateAudioItem]
  )

  const handleDuplicate = useCallback(() => {
    const activeItem = activeMediaItem || activeAudioItem

    if (activeItem) {
      duplicateTimelineItem(activeItem)
    }
  }, [activeMediaItem, activeAudioItem, duplicateTimelineItem])

  const handleCopy = useCallback(() => {
    if (!isDisabled) {
      dispatch(projectDataThunks.copyTimelineItem())
    }
  }, [isDisabled])

  const handlePaste = useCallback(() => {
    if (!clipboardItem) return

    duplicateTimelineItem(clipboardItem)
  }, [clipboardItem, duplicateTimelineItem])

  // Keyboard shortcuts: Ctrl+C (or Cmd+C on Mac) for copy, Ctrl+V (or Cmd+V on Mac) for paste
  // Scoped to timeline container to avoid conflicts with text inputs
  useKeyPressWithConfigs('c', handleCopy, {
    configs: { metaKey: true },
    targetAreaSelector: '.timeline-container'
  })
  useKeyPressWithConfigs('v', handlePaste, {
    configs: { metaKey: true },
    targetAreaSelector: '.timeline-container'
  })

  return (
    <ActionButton
      icon={<DuplicateIcon />}
      disabled={isDisabled}
      tooltipText={translate(T.DUPLICATE_LABEL)}
      onClick={handleDuplicate}
      title={isMobile ? translate(T.DUPLICATE_LABEL) : undefined}
    />
  )
}
